import { FILTER_TREE_TYPE, SEARCH_FILTER_NAME } from 'constants/filter';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';

//
// --- Types ---

export type DiversityTypeMap = {
  [key: string]: DiversityTypeMapEntry;
};

type DiversityTypeMapEntry = {
  category: string;
  name: string;
};

type TransformedFilterTreeNodeMap = {
  [key: string]: TransformedFilterTreeNode;
};

//
// --- Constants ---

const DEFAULT_DIVERSITY_HIERARCHY_NODE = {
  checked: false,
  children: [],
  filterTreeType: FILTER_TREE_TYPE.DIVERSE,
  leaf: false,
  open: false
};

const DEFAULT_DIVERSITY_HIERARCHY_LEAF_NODE = {
  ...DEFAULT_DIVERSITY_HIERARCHY_NODE,
  leaf: true
};

//
// --- Diversity Hierarchy Functions ---

export function transformDiversityTypeMapToTreeNodes(
  diversityTypesMap: DiversityTypeMap
): ReadonlyArray<TransformedFilterTreeNode> {
  // create a map object of category name to tree nodes
  const childrenByCategoryMap = Object.keys(diversityTypesMap).reduce<{
    [key: string]: TransformedFilterTreeNode;
  }>((categoryTreeNodeMap: TransformedFilterTreeNodeMap, currentDiversityTypeKey: string) => {
    const currentDiversityType = diversityTypesMap[currentDiversityTypeKey];
    const currentCategory = currentDiversityType.category;

    const newDiversityNode = createTransformedDiversityNode(currentDiversityTypeKey, currentDiversityType.name);
    const currentCategoryTreeNode = categoryTreeNodeMap[currentCategory];

    let updatedDiversityNode;
    if (currentCategoryTreeNode) {
      updatedDiversityNode =
        currentCategoryTreeNode.children.length > 0
          ? // already has children, so append new node as another child
            {
              ...currentCategoryTreeNode,
              children: [...currentCategoryTreeNode.children, { ...newDiversityNode, parentId: currentCategory }]
            }
          : // does not have children, need to split into 2 children
            {
              ...currentCategoryTreeNode,
              children: [
                {
                  ...createTransformedDiversityNode(
                    currentCategoryTreeNode.id,
                    currentCategoryTreeNode.name ? currentCategoryTreeNode.name : '',
                    {
                      parentId: currentCategory
                    }
                  )
                },
                { ...newDiversityNode, parentId: currentCategory }
              ],
              id: currentCategory,
              leaf: false,
              name: currentCategory || ''
            };
    }

    return { ...categoryTreeNodeMap, [currentCategory]: updatedDiversityNode || newDiversityNode };
  }, {});

  // Finally return the calculated map into an array and put underneath
  // the root Diversity Node.
  return [
    {
      ...DEFAULT_DIVERSITY_HIERARCHY_NODE,
      children: Object.values(childrenByCategoryMap),
      filterPrimaryKey: SEARCH_FILTER_NAME.DIVERSITY_STATUS,
      id: SEARCH_FILTER_NAME.DIVERSITY_STATUS,
      isCategoryNode: true,
      name: 'Diversity Database',
      open: true,
      parentId: null,
      showInfoIcon: true,
      tooltip:
        "Supplier companies in the Wood Mackenzie Supply Chain FactBase that have been flagged as diverse. Note that not all suppliers may meet your company's definition of diverse."
    }
  ];
}

function createTransformedDiversityNode(id: string, name: string, opts: { parentId?: string } = {}) {
  const { parentId = SEARCH_FILTER_NAME.DIVERSITY_STATUS } = opts;

  return {
    ...DEFAULT_DIVERSITY_HIERARCHY_LEAF_NODE,
    id,
    name,
    parentId
  };
}
